import React, { useRef, useEffect, useState, useCallback } from 'react'

interface GlowCursorProps {
  className?: string
}

interface TrailPoint {
  x: number
  y: number
  opacity: number
}

export const GlowCursor: React.FC<GlowCursorProps> = ({ className = '' }) => {
  const [position, setPosition] = useState({ x: -100, y: -100 })
  const [isHovering, setIsHovering] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const trailRef = useRef<TrailPoint[]>([])
  const [trail, setTrail] = useState<TrailPoint[]>([])
  const animFrameRef = useRef<number>(0)
  const isTouchRef = useRef(false)

  const handleMouseMove = useCallback((e: MouseEvent) => {
    setPosition({ x: e.clientX, y: e.clientY })
    setIsVisible(true)

    trailRef.current.push({ x: e.clientX, y: e.clientY, opacity: 1 })
    if (trailRef.current.length > 15) {
      trailRef.current = trailRef.current.slice(-15)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false)
  }, [])

  const handleMouseEnter = useCallback(() => {
    setIsVisible(true)
  }, [])

  useEffect(() => {
    // Detect touch device
    isTouchRef.current =
      'ontouchstart' in window || navigator.maxTouchPoints > 0

    if (isTouchRef.current) return

    window.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseleave', handleMouseLeave)
    document.addEventListener('mouseenter', handleMouseEnter)

    // Track hoverable elements
    const handleElementMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.classList.contains('cursor-pointer') ||
        getComputedStyle(target).cursor === 'pointer'
      ) {
        setIsHovering(true)
      }
    }

    const handleElementMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.classList.contains('cursor-pointer') ||
        getComputedStyle(target).cursor === 'pointer'
      ) {
        setIsHovering(false)
      }
    }

    document.addEventListener('mouseover', handleElementMouseOver)
    document.addEventListener('mouseout', handleElementMouseOut)

    const animate = () => {
      trailRef.current = trailRef.current
        .map((point) => ({ ...point, opacity: point.opacity - 0.08 }))
        .filter((point) => point.opacity > 0)
      setTrail([...trailRef.current])
      animFrameRef.current = requestAnimationFrame(animate)
    }

    animFrameRef.current = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animFrameRef.current)
      window.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('mouseenter', handleMouseEnter)
      document.removeEventListener('mouseover', handleElementMouseOver)
      document.removeEventListener('mouseout', handleElementMouseOut)
    }
  }, [handleMouseMove, handleMouseLeave, handleMouseEnter])

  // Inject global cursor-hide CSS
  useEffect(() => {
    if (isTouchRef.current) return

    const styleId = 'glow-cursor-style'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @media (hover: hover) and (pointer: fine) {
          * { cursor: none !important; }
        }
      `
      document.head.appendChild(style)
    }

    return () => {
      const existing = document.getElementById(styleId)
      if (existing) {
        document.head.removeChild(existing)
      }
    }
  }, [])

  // Don't render on touch devices
  if (typeof window !== 'undefined' && isTouchRef.current) {
    return null
  }

  const cursorSize = isHovering ? 48 : 16
  const glowColor = isHovering ? '#06b6d4' : '#a855f7'

  return (
    <div className={`fixed inset-0 pointer-events-none z-[9999] ${className}`}>
      {/* Trail particles */}
      {trail.map((point, index) => (
        <div
          key={index}
          className="absolute rounded-full"
          style={{
            left: point.x,
            top: point.y,
            width: 6,
            height: 6,
            transform: 'translate(-50%, -50%)',
            backgroundColor: glowColor,
            opacity: point.opacity * 0.4,
            boxShadow: `0 0 8px ${glowColor}`,
          }}
        />
      ))}

      {/* Main cursor */}
      <div
        className="absolute rounded-full transition-[width,height] duration-200 ease-out"
        style={{
          left: position.x,
          top: position.y,
          width: cursorSize,
          height: cursorSize,
          transform: 'translate(-50%, -50%)',
          backgroundColor: glowColor,
          opacity: isVisible ? 0.9 : 0,
          boxShadow: `
            0 0 20px ${glowColor},
            0 0 40px ${glowColor}80,
            0 0 60px ${glowColor}40
          `,
          transitionProperty: 'width, height, opacity',
        }}
      />
    </div>
  )
}
